import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { useApiContext } from 'context/ApiContext';
import { useGlobalContext } from 'context/GlobalContext';
import { KEYS } from 'helpers/react-query.config';
import { AxiosServiceError } from 'interfaces/general';
import { ISurveyTemplate, SaveanswerSurvey } from 'api/insight';

const SurveyMutationCRUD = ({
  isDeleting = false,
  callbackCreateSuccess,
  callbackUpdateSuccess
}: {
  isDeleting?: boolean;
  callbackCreateSuccess?: () => void;
  callbackUpdateSuccess?: () => void;
}) => {
  const { showApiError, showSuccess } = useGlobalContext();
  const { insight: insightApi } = useApiContext();
  const queryClient = useQueryClient();
  const { t } = useTranslation('global');

  const create = useMutation(
    (data: ISurveyTemplate) => insightApi.survey.createTemplate(data),
    {
      onError: (error: AxiosServiceError) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      },
      onSuccess: () => {
        if (callbackCreateSuccess) callbackCreateSuccess();
        showSuccess(t('survey.messages.createSuccess'));
      }
    }
  );

  const update = useMutation(
    (data: ISurveyTemplate) => insightApi.survey.updateTemplate(data),
    {
      onError: (error: AxiosServiceError) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(KEYS.insight.survey.getAllTemplate);
        if (callbackUpdateSuccess) callbackUpdateSuccess();

        if (isDeleting) showSuccess(t('survey.messages.deleteSuccess'));
        else showSuccess(t('survey.messages.updateSuccess'));
      }
    }
  );

  const answerSurvey = useMutation(
    (data: SaveanswerSurvey) => insightApi.survey.SaveanswerSurvey(data),
    {
      onError: (error: AxiosServiceError) => {
        showApiError(error, t('common.commonMessages.genericRequestError'));
      },
      onSuccess: () => {
        showSuccess(t('survey.messages.sendSurveySuccess'));
      }
    }
  );

  return { 
    create, 
    update, 
    answerSurvey, 
    isCreate: answerSurvey?.isSuccess, 
    isLoading: update?.isLoading || create?.isLoading 
  };
};

export default SurveyMutationCRUD;
